import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import EmptyState from "@/components/base/EmptyState";
import WorkCard from "@/components/feature/WorkCard";
import useSEO from "@/hooks/useSEO";
import { getActressById, getActressNames, getNewsById, getWorkById } from "@/utils/data";
import type { Actress } from "@/mocks/actresses";
import type { Work } from "@/mocks/works";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const news = id ? getNewsById(id) : undefined;

  const relatedActresses = news
    ? news.relatedActressIds
        .map((actressId) => getActressById(actressId))
        .filter((item): item is Actress => Boolean(item))
    : [];

  const relatedWorks = news
    ? news.relatedWorkIds
        .map((workId) => getWorkById(workId))
        .filter((item): item is Work => Boolean(item))
    : [];

  useSEO({
    title: news ? `${news.title} - 新闻动态 - 日本女优大全` : "未找到该新闻 - 日本女优大全",
    description: news ? news.summary : "该新闻条目不存在或已调整，请返回新闻动态列表浏览其他资讯。",
    keywords: news ? `${news.category},日本女优新闻,${news.title}` : "日本女优新闻,新闻动态",
  });

  if (!news) {
    return (
      <SiteLayout>
        <Breadcrumbs items={[{ label: "新闻动态", href: "/news" }, { label: "未找到新闻" }]} />
        <EmptyState
          icon="ri-file-warning-line"
          title="未找到该新闻"
          description="你访问的新闻条目不存在，可能链接有误或内容已调整。可以返回新闻列表查看最新资讯。"
          actionLabel="返回新闻列表"
          actionHref="/news"
        />
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <Breadcrumbs items={[{ label: "新闻动态", href: "/news" }, { label: news.title }]} />

      <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
        <article>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-500 px-2.5 py-1 font-label text-[11px] text-background-50">
                {news.category}
              </span>
              <span className="font-label text-xs text-foreground-500">{news.date}</span>
              <span className="font-label text-xs text-foreground-300">·</span>
              <span className="font-label text-xs text-foreground-500">资料编辑部</span>
            </div>

            <h1 className="mt-4 font-heading text-[26px] font-black leading-snug text-foreground-950 md:text-[32px]">
              {news.title}
            </h1>

            <p className="mt-4 border-l-[3px] border-accent-500 bg-accent-50 px-4 py-3 text-sm leading-relaxed text-foreground-700">
              {news.summary}
            </p>

            <div className="mt-6 space-y-4">
              {news.content.map((paragraph, index) => (
                <p key={index} className="text-[15px] leading-8 text-foreground-800">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-5">
              <Link
                to="/news"
                className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-arrow-left-line" aria-hidden="true"></i>
                返回新闻列表
              </Link>
              <span className="font-label text-[11px] text-foreground-400">
                本文为站内示例资讯，用于演示新闻浏览与关联阅读。
              </span>
            </div>
          </div>

          {relatedActresses.length > 0 ? (
            <section className="mt-8">
              <h2 className="border-b border-background-200 pb-3 font-heading text-lg font-bold text-foreground-950">
                相关女优
              </h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {relatedActresses.map((actress) => (
                  <Link
                    key={actress.id}
                    to={`/detail/actress/${actress.id}`}
                    className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                  >
                    <span className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={actress.portrait}
                        alt={`${actress.name} 女优资料写真`}
                        title={`${actress.name} 日本女优资料`}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-label text-sm text-foreground-900 transition-colors group-hover:text-primary-700">
                        {actress.name}
                      </span>
                      <span className="mt-0.5 block truncate font-label text-[11px] text-foreground-500">
                        {actress.agency} · {actress.debutYear} 年出道
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-300" aria-hidden="true"></i>
                  </Link>
                ))}
              </div>
            </section>
          ) : null}

          {relatedWorks.length > 0 ? (
            <section className="mt-8">
              <h2 className="border-b border-background-200 pb-3 font-heading text-lg font-bold text-foreground-950">
                相关作品
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
                {relatedWorks.map((work) => (
                  <WorkCard key={work.id} work={work} actressNames={getActressNames(work)} />
                ))}
              </div>
            </section>
          ) : null}
        </article>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">资讯信息</h2>
            <dl className="mt-3 space-y-2.5">
              {[
                { label: "栏目", value: news.category },
                { label: "发布日期", value: news.date },
                { label: "来源", value: "资料编辑部" },
                { label: "关联女优", value: `${relatedActresses.length} 位` },
                { label: "关联作品", value: `${relatedWorks.length} 部` },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex items-start justify-between gap-3 border-b border-background-100 pb-2 last:border-b-0 last:pb-0"
                >
                  <dt className="font-label text-[11px] text-foreground-500">{row.label}</dt>
                  <dd className="text-right font-label text-xs text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">继续浏览</h2>
            <ul className="mt-3 space-y-2">
              <li>
                <Link
                  to="/news"
                  className="flex items-center justify-between rounded-md px-2 py-2 font-label text-xs text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                >
                  全部新闻动态
                  <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                </Link>
              </li>
              <li>
                <Link
                  to="/work-list"
                  className="flex items-center justify-between rounded-md px-2 py-2 font-label text-xs text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                >
                  影视作品列表
                  <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                </Link>
              </li>
              <li>
                <Link
                  to="/category"
                  className="flex items-center justify-between rounded-md px-2 py-2 font-label text-xs text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                >
                  女优分类档案
                  <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                </Link>
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}